import throttle from 'lodash-es/throttle';
import mitt from 'mitt';

type IdleUserState = 'active' | 'idle' | null;

type IdleDetectorOptions = {
  threshold: number; // in milliseconds
  recordActivityThreshold?: number; // throttle threshold for recording activity, defaults to Math.min(threshold / 2, 5000)
  debug?: boolean; // enable debug logging for state changes and activity
};

// DOM has an IdleDetector API, but it's not yet widely supported and requires an explicit user permission (like Notifications).
export class WeaveIdleDetector {
  private emitter = mitt<{ change: IdleUserState }>();
  private lastActiveAt: number | null = null;
  private options: IdleDetectorOptions;
  private listenersRegistered = false;
  private recordActivityThreshold: number;
  private idleTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private lastState: IdleUserState = null;
  private debug: boolean;
  private recordActivity!: ReturnType<typeof throttle>;

  constructor(options: IdleDetectorOptions) {
    this.options = options;
    this.debug = options.debug ?? false;
    this.recordActivityThreshold = options.recordActivityThreshold ?? Math.min(options.threshold / 2, 5000);

    // Initialize recordActivity after recordActivityThreshold is set to avoid lint error
    this.recordActivity = throttle(
      () => {
        this.lastActiveAt = Date.now();

        const state = this.getIdleState();
        if (state !== this.lastState) {
          if (this.debug) {
            console.log(`[WeaveIdleDetector] State change: ${this.lastState} → ${state}`);
          }
          this.lastState = state;
          this.emitter.emit('change', state);
        }

        this.scheduleIdleTimeout();
      },
      this.recordActivityThreshold,
      { leading: true, trailing: true }
    );
  }

  private clearIdleTimeout() {
    if (this.idleTimeoutId) {
      clearTimeout(this.idleTimeoutId);
      this.idleTimeoutId = null;
    }
  }

  private scheduleIdleTimeout() {
    this.clearIdleTimeout();
    this.idleTimeoutId = setTimeout(() => {
      if (this.isIdle() && this.lastState !== 'idle') {
        if (this.debug) {
          console.log(`[WeaveIdleDetector] Idle timeout triggered: ${this.lastState} → idle`);
        }
        this.lastState = 'idle';
        this.emitter.emit('change', 'idle');
      }
    }, this.options.threshold);
  }

  private handleVisibilityChange = () => {
    if (document.hidden) {
      // When tab becomes hidden, clear the timeout to save resources
      if (this.debug) {
        console.log('[WeaveIdleDetector] Tab hidden, pausing idle detection');
      }
      this.clearIdleTimeout();
    } else {
      // When tab becomes visible again, treat it as user activity
      if (this.debug) {
        console.log('[WeaveIdleDetector] Tab visible, resuming idle detection');
      }
      this.recordActivity();
    }
  };

  getIsListening() {
    return this.listenersRegistered;
  }

  getIdleState(): IdleUserState {
    if (this.lastActiveAt === null) return 'idle';
    return Date.now() - this.lastActiveAt >= this.options.threshold ? 'idle' : 'active';
  }

  isIdle() {
    return this.getIdleState() === 'idle';
  }

  /**
   * Start listening for user activity and idle detection.
   * onChange will automatically start the detector if not already started. Prefer using that method instead.
   */
  start() {
    if (this.listenersRegistered) return;

    if (this.debug) {
      console.log('[WeaveIdleDetector] Starting idle detection');
    }

    this.listenersRegistered = true;

    // Record initial activity to avoid starting in idle state
    this.recordActivity();

    // Mouse and keyboard events
    window.addEventListener('mousemove', this.recordActivity, { passive: true });
    window.addEventListener('keydown', this.recordActivity, { passive: true });
    window.addEventListener('click', this.recordActivity, { passive: true });
    window.addEventListener('scroll', this.recordActivity, { passive: true });

    // Touch events for mobile
    window.addEventListener('touchstart', this.recordActivity, { passive: true });
    window.addEventListener('touchmove', this.recordActivity, { passive: true });

    // Page visibility
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  off() {
    if (this.debug) {
      console.log('[WeaveIdleDetector] Stopping idle detection');
    }

    this.listenersRegistered = false;
    this.clearIdleTimeout();
    this.recordActivity.cancel();

    window.removeEventListener('mousemove', this.recordActivity);
    window.removeEventListener('keydown', this.recordActivity);
    window.removeEventListener('click', this.recordActivity);
    window.removeEventListener('scroll', this.recordActivity);
    window.removeEventListener('touchstart', this.recordActivity);
    window.removeEventListener('touchmove', this.recordActivity);

    document.removeEventListener('visibilitychange', this.handleVisibilityChange);

    // Clear all emitter subscriptions and reset state
    this.emitter.all.clear();
    this.lastActiveAt = null;
    this.lastState = null;
  }

  /**
   * Subscribe to idle state changes. This automatically starts the detector if not already started.
   * The callback is immediately invoked with the current state.
   * Returns unsubscribe function.
   */
  onChange(callback: (state: IdleUserState) => void) {
    if (!this.listenersRegistered) {
      this.start();
    }

    // Immediately invoke callback with current state
    const currentState = this.getIdleState();
    if (this.debug) {
      console.log(`[WeaveIdleDetector] New subscriber, current state: ${currentState}`);
    }
    callback(currentState);

    this.emitter.on('change', callback);
    return () => {
      this.emitter.off('change', callback);
      const changeHandlers = this.emitter.all.get('change');
      if (changeHandlers?.length === 0) {
        this.off();
      }
    };
  }
}
